import { Link } from "@tanstack/react-router";
import { ChevronsLeft } from "lucide-react";
import { cn, Tooltip } from "@platform/ui";
import { useConsole } from "../context";
import { useT } from "../i18n";
import { OVERVIEW, SECTIONS, type NavItem } from "../nav";

function Item({
  item,
  active,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  onNavigate?: (() => void) | undefined;
}) {
  const { t } = useT();
  const Icon = item.icon;
  const label = t(item.label);
  const link = (
    <Link
      to={item.to}
      onClick={onNavigate}
      data-testid={`nav-${item.id}`}
      aria-current={active ? "page" : undefined}
      className={cn(
        "mx-3 flex items-center gap-3 rounded-lg py-2 text-sm whitespace-nowrap transition-colors",
        collapsed ? "justify-center px-0" : "px-3",
        active
          ? "bg-white/10 font-medium text-white shadow-[inset_0_0_0_1px_rgb(255_255_255/0.06)]"
          : "text-nav-text hover:bg-nav-hover hover:text-white",
      )}
    >
      <Icon aria-hidden className={cn("size-[18px] shrink-0", active && "text-nav-active")} />
      <span className={cn(collapsed && "sr-only")}>{label}</span>
    </Link>
  );
  return (
    <li>
      {collapsed ? (
        <Tooltip content={label} side="right">
          {link}
        </Tooltip>
      ) : (
        link
      )}
    </li>
  );
}

export function Navigator({
  pathname,
  onNavigate,
  collapsed = false,
  onToggleCollapsed,
}: {
  pathname: string;
  onNavigate?: () => void;
  collapsed?: boolean;
  /** Desktop only: renders the collapse/expand button pinned to the bottom of the sidebar. */
  onToggleCollapsed?: () => void;
}) {
  const { t } = useT();
  const { productName, can } = useConsole();
  const isActive = (i: NavItem) =>
    i.to === "/" ? pathname === "/" : pathname === i.to || pathname.startsWith(`${i.to}/`);
  const toggleLabel = t(collapsed ? "nav.expand" : "nav.collapse");
  const toggle = onToggleCollapsed && (
    <button
      type="button"
      onClick={onToggleCollapsed}
      aria-expanded={!collapsed}
      aria-label={toggleLabel}
      data-testid="nav-toggle"
      className={cn(
        "group flex h-12 w-full items-center gap-3 text-sm text-nav-text transition-colors hover:text-white",
        collapsed ? "justify-center" : "px-5",
      )}
    >
      <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-md bg-nav-hover transition-colors group-hover:bg-white/15">
        <ChevronsLeft
          aria-hidden
          className={cn("size-4 transition-transform duration-200", collapsed && "rotate-180")}
        />
      </span>
      {!collapsed && <span>{toggleLabel}</span>}
    </button>
  );
  return (
    <nav aria-label={t("nav.main")} className="flex min-h-full flex-col bg-nav">
      <div
        title={collapsed ? productName : undefined}
        className={cn(
          "flex h-16 items-center border-b border-nav-divider font-medium text-white",
          collapsed ? "justify-center text-xl" : "px-6 text-[22px]",
        )}
      >
        {collapsed ? (
          <>
            <span aria-hidden>{productName.charAt(0).toUpperCase()}</span>
            <span className="sr-only">{productName}</span>
          </>
        ) : (
          <span className="truncate">{productName}</span>
        )}
      </div>
      <ul className="space-y-0.5 border-b border-nav-divider bg-nav-item py-2">
        <Item item={OVERVIEW} active={isActive(OVERVIEW)} collapsed={collapsed} onNavigate={onNavigate} />
      </ul>
      {SECTIONS.map((s) => {
        const items = s.items.filter((i) => can(i.permission));
        if (!items.length) return null;
        return (
          <div key={s.id} className={cn("border-b border-nav-divider bg-nav-item pb-2", collapsed && "pt-2")}>
            <p
              className={cn(
                collapsed
                  ? "sr-only"
                  : "truncate px-6 pt-4 pb-2 text-xs font-semibold tracking-wide text-white/50",
              )}
            >
              {t(s.label)}
            </p>
            <ul className="space-y-0.5">
              {items.map((i) => (
                <Item
                  key={i.id}
                  item={i}
                  active={isActive(i)}
                  collapsed={collapsed}
                  onNavigate={onNavigate}
                />
              ))}
            </ul>
          </div>
        );
      })}
      {toggle && (
        <div className="sticky bottom-0 mt-auto border-t border-nav-divider bg-nav">
          {collapsed ? (
            <Tooltip content={toggleLabel} side="right">
              {toggle}
            </Tooltip>
          ) : (
            toggle
          )}
        </div>
      )}
    </nav>
  );
}
